
import {
  ref,
  onMounted,
  useContext,
  useRouter,
  useFetch,
  useRoute,
  watch,
  computed
} from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore';
import useVariantDetails from '@/composable/useVariantDetails';
import useTlmCart from '@/composable/useTlmCart';
import useProducts from '@/composable/useProducts';
import usePrices from '~/composable/usePrices';
import useTracking from '~/composable/tracking/useTracking';
import usePlaymix from '@/composable/usePlaymix'
import useInventory from '~/composable/checkout/validations/useInventory';
import useColor from '~/composable/useColor';
import useCategories from '@/composable/useCategories';
import useHeylink from '@/composable/useHeylink';
import { OFFERED_TO_CUSTOMER_TYPE, OFFER_MESSAGES } from '@/constants/OfferConstants';


export default {
  setup() {
    const { track } = useTracking()
    const { getBackground } = useColor()
    const { app } = useContext()
    const router = useRouter()
    const route = useRoute()
    const { addCartItem, loadOrCreateCart } = useTlmCart(app);
    const { getCategory, getCategorySlugById } = useCategories(app.$hubble)
    const { getProductDetails } = useProducts(app.$hubble)
    const { getPricePerSku } = usePrices(app.$hubble)
    const { getInventory } = useInventory(app)
    const heylink = useHeylink()
    const { getHasSpecialPrices, hasSpecialPrices, offeredToCustomerType, getOfferedToCustomerTypeInfo } = useCategories(app.$hubble)

    const chosenVariantDetailsStore = useChosenVariantDetailsStore()
    const {
      chosenStorage,
      chosenColor,
      chosenChannel,
      chosenInstallment,
      productDetails
    } = storeToRefs(chosenVariantDetailsStore)
    const { selectedPlayServices } = usePlaymix('playmix-4', app.$hubble)

    const categorySlug = route.value.params.categoryslug.toLowerCase()
    const productSlug = route.value.params.productslug

    const productDetailsLoading = ref(true)
    const productVariants = ref(null)

    const redirecIfNotFound = () => {
      if (process.client) {
        location.href = location.origin + '/shop/' + categorySlug
      } else {
        router.push('/' + categorySlug)
      }
    }

    const { fetch, fetchState } = useFetch(async () => {
      productVariants.value = await getProductDetails(categorySlug, productSlug).catch(() => {
        redirecIfNotFound()
      })
      if (!productVariants.value) {
        redirecIfNotFound()
        throw new Error('Failed to fetch product details page')
      }
      const variantDetails = useVariantDetails(productVariants, chosenColor, chosenStorage, true, getPricePerSku, productSlug, getInventory, categorySlug)
      productDetails.value = variantDetails
      productDetailsLoading.value = false
      await getHasSpecialPrices({ categorySlug })
      await getOfferedToCustomerTypeInfo({ categorySlug })
    }, route.value.params.categoryslug + route.value.params.productslug)

    fetch()
    const trackPageLoad = () => {
      setTimeout(() => { // wait for prices etc.
        track('loadProductsDetailsPage', { productDetails, chosenInstallment, chosenChannel, categorySlug, getCategorySlugById })
      }, 800)
    }
    onMounted(() => {
      loadOrCreateCart();
      watch(fetchState, () => {
        if (fetchState.error) {
          redirecIfNotFound()
          return
        }
        trackPageLoad()
      })
      heylink.storeParamsToSession(route.value.query)
    })


    const addToBasketBtnClicked = async () => {
      const category = await getCategory({ categorySlug })
      const categoryId = category.id
      // channel key 'Cash' - whether to be used or not to be decided
      addCartItem({
        sku: productDetails.value.chosenVariantSku,
        channelKey: chosenChannel?.value?.channelKey === 'Cash' ? '' : chosenChannel?.value?.channelKey,
        installmentPeriod: chosenChannel?.value?.channelKey === 'Cash' ? 0 : chosenInstallment?.value?.months,
        mixProducts: chosenChannel?.value?.subscription?.isMixProduct ? selectedPlayServices.value : null,
        categoryId
      })
        .then((res) => {
          if (res) {
            router.push('/shopkoeb')
          }
        })
    }

    const getVideo = (nr) => {
      const videos = productDetails?.value.masterProduct?.productContent?.videos
      return videos?.at(nr)
    }

    const imageArrowClick = (eventData) => {
      track('carouselArrowClick', { ...eventData, productDetails, categorySlug, chosenChannel, chosenInstallment })
    }

    const campaignCircleItemText = computed(() => {
      if (offeredToCustomerType?.value !== undefined && offeredToCustomerType?.value !== null) {
        return getCampaignCircleItemText()
      }
    })

    const getCampaignCircleItemText = () => {
      if (offeredToCustomerType?.value?.length > 1) {
        return { text: OFFER_MESSAGES[OFFERED_TO_CUSTOMER_TYPE.EXISTING] }
      } else if (offeredToCustomerType?.value?.includes(OFFERED_TO_CUSTOMER_TYPE.NUMBER_PORTING)) {
        return { text: OFFER_MESSAGES[OFFERED_TO_CUSTOMER_TYPE.NUMBER_PORTING] }
      } else if (offeredToCustomerType?.value?.includes(OFFERED_TO_CUSTOMER_TYPE.NEW_CUSTOMER)) {
        return { text: OFFER_MESSAGES[OFFERED_TO_CUSTOMER_TYPE.NEW_CUSTOMER] }
      } else if (offeredToCustomerType?.value?.includes(OFFERED_TO_CUSTOMER_TYPE.EXISTING)) {
        return { text: OFFER_MESSAGES[OFFERED_TO_CUSTOMER_TYPE.EXISTING] }
      }
    }

    const campaignCircleItemPrice = computed(() => {
      if (chosenChannel.value?.productBundlePrice?.productWithSubscriptionPrice === 0) {
        return { text: 'Til 0 kr.' }
      } else {
        return { text: 'Til en skarp pris' }
      }
    })

    return {
      offeredToCustomerType,
      campaignCircleItemPrice,
      campaignCircleItemText,
      categorySlug,
      hasSpecialPrices,
      getBackground,
      imageArrowClick,
      getVideo,
      track,
      productDetailsLoading,
      productDetails,
      addToBasketBtnClicked
    }
  },
  // eslint-disable-next-line complexity
  head() {
    if (this.hasSpecialPrices) {
      return {
        title: this.productDetails.metaTitle,
        meta: [
          {
            hid: 'robots',
            name: 'robots',
            content: 'noindex, nofollow'
          }
        ]
      }
    }

    if (!this.productDetails.metaTitle) {
      return false
    }

    const date = new Date();

    return {
      title: this.productDetails.metaTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.productDetails.metaDescription
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.productDetails.metaDescription
        }
      ],
      script: [{
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org/',
          '@type': 'Product',
          name: this.productDetails?.metaTitle || this.productDetails?.chosenVariant?.metaTitle || this.productDetails?.chosenVariant?.name,
          description: this.productDetails?.metaDescription || this.productDetails?.chosenVariant?.metaDescription,
          image: this.productDetails?.masterProductImages?.map(img => img.imageUrl + '?w=500&h=500&fit=pad'),
          sku: this.productDetails?.chosenVariantSku,
          mpn: this.productDetails?.chosenVariant?.productAttributes?.find(a => a.name === 'manufacturer-id')?.value,
          brand: {
            '@type': 'Brand',
            name: this.productDetails?.productBrand
          },
          offers: {
            '@type': 'Offer',
            priceValidUntil: `${date.getFullYear()}-${date.getDate()}-${date.getMonth() + 1}}`,
            price: this.productDetails?.chosenVariantBasePrice || this.productDetails?.chosenVariantCashPrice || '',
            priceCurrency: 'DKK'
          }
        })
      }],
      __dangerouslyDisableSanitizers: ['script']
    }
  }
}
